import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import config from "../config.js";
import { fetchData } from "../utils/api.js";
import { initMapKit, initMapKitAsync } from "../utils/mapkit.js";
import { buildAppleMapsUrl } from "../utils/scheduleHelpers.js";
import { FieldIcon, LocationIcon } from "./common/Icons.js";
import EditIcon from "./common/EditIcon.js";
import TrashIcon from "./common/TrashIcon.js";
import { Drawer } from "./common/Drawer.js";
import ConfirmationModal from "./common/ConfirmationModal.js";
import { LocationPicker } from "./common/LocationPicker.js";
import "./LeagueLocations.css";
import { Button } from "./ui/Button";

const StaticMap = ({ address, isMapKitReady }) => {
  const mapRef = React.useRef(null);
  const mapContainerRef = React.useRef(null);

  React.useEffect(() => {
    if (!isMapKitReady || !globalThis.mapkit || !address) return;
    if (mapRef.current) return;

    // Slight delay to ensure mapkit is fully ready in DOM
    const initTimer = setTimeout(() => {
      try {
        const hiddenVisibility = globalThis.mapkit.FeatureVisibility?.Hidden;
        const mapOptions = {
          showsUserLocationControl: false,
          showsZoomControl: false,
          showsMapTypeControl: false,
          isScrollEnabled: false,
          isZoomEnabled: false,
          isRotationEnabled: false,
        };
        if (hiddenVisibility !== undefined) {
          mapOptions.showsCompass = hiddenVisibility;
          mapOptions.showsScale = hiddenVisibility;
        }
        const map = new globalThis.mapkit.Map(
          mapContainerRef.current,
          mapOptions,
        );
        mapRef.current = map;
        const search = new globalThis.mapkit.Search();
        search.search(address, (error, data) => {
          if (!error && data.places && data.places.length > 0) {
            const place = data.places[0];
            const coordinate = place.coordinate;
            map.region = new globalThis.mapkit.CoordinateRegion(
              coordinate,
              new globalThis.mapkit.CoordinateSpan(0.01, 0.01),
            );
            const annotation = new globalThis.mapkit.MarkerAnnotation(
              coordinate,
              {
                color: "#40e0d0",
              },
            );
            map.addAnnotation(annotation);
          }
        });
      } catch (e) {
        console.warn("Static map init failed:", e);
      }
    }, 100);

    return () => {
      clearTimeout(initTimer);
      if (mapRef.current) {
        try {
          mapRef.current.destroy();
        } catch (e) {}
        mapRef.current = null;
      }
    };
  }, [address, isMapKitReady]);

  if (!isMapKitReady || !globalThis.mapkit) return null;

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: "120px",
        height: "90px",
        borderRadius: "8px",
        overflow: "hidden",
        opacity: 0.9,
        backgroundColor: "var(--color-bg-subtle)",
      }}
    />
  );
};

StaticMap.propTypes = {
  address: PropTypes.string,
  isMapKitReady: PropTypes.bool,
};

// Extracted LocationCard Component (Flat Row)
const LocationCard = ({
  location,
  onEdit,
  onDelete,
  onAddField,
  canEdit,
  isMapKitReady,
}) => {
  const [isAddingField, setIsAddingField] = useState(false);
  const [fieldName, setFieldName] = useState("");

  const handleAdd = (e) => {
    e.preventDefault();
    if (fieldName.trim()) {
      onAddField(location, fieldName.trim());
      setFieldName("");
      setIsAddingField(false);
    }
  };

  const venueMapsUrl = buildAppleMapsUrl(location.name, location.address);
  const showInteractiveMap = Boolean(
    location.address && isMapKitReady && globalThis.mapkit,
  );

  return (
    <div
      className="location-row"
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "16px",
        padding: "16px 0",
        borderBottom: "1px solid var(--color-border)",
        background: "transparent",
      }}
    >
      <div style={{ flexShrink: 0 }}>
        {showInteractiveMap ? (
          <StaticMap address={location.address} isMapKitReady={isMapKitReady} />
        ) : (
          <a
            href={venueMapsUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${location.name || "location"} in Maps`}
            style={{
              width: "84px",
              height: "84px",
              borderRadius: "10px",
              border: "1px solid var(--color-border)",
              background: "var(--color-bg-subtle)",
              color: "var(--color-text-secondary)",
              fontSize: "0.85rem",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              textDecoration: "none",
            }}
          >
            map
          </a>
        )}
      </div>
      <div
        className="location-content"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          flex: 1,
        }}
      >
        <div
          className="location-info"
          style={{ display: "flex", flexDirection: "column", gap: "4px" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <LocationIcon size="xs" />
            {venueMapsUrl ? (
              <a
                href={venueMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="location-name location-address-link"
                style={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "var(--color-text-primary)",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                }}
              >
                {location.name}
              </a>
            ) : (
              <span
                className="location-name"
                style={{ fontSize: "1rem", fontWeight: 600 }}
              >
                {location.name}
              </span>
            )}
          </div>
          {location.address && location.address !== location.name && (
            <div
              className="location-address"
              style={{
                paddingLeft: "24px",
                color: "var(--color-text-secondary)",
                fontSize: "0.85rem",
              }}
            >
              <span>{location.address}</span>
            </div>
          )}
        </div>

        <div
          className="field-list"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8px 14px",
          }}
        >
          <FieldIcon size="xs" />
          {location.fields && location.fields.length > 0 ? (
            location.fields.map((field, idx) => {
              const fLabel =
                typeof field === "string"
                  ? field
                  : field?.field_name || field?.name;
              const fieldMapsUrl = buildAppleMapsUrl(
                `${location.name || ""} ${fLabel || ""}`.trim(),
                location.address,
              );
              return (
                <a
                  key={
                    typeof field === "string"
                      ? field
                      : field?.field_id ||
                        field?.id ||
                        field?.field_name ||
                        field?.name ||
                        idx
                  }
                  href={fieldMapsUrl || venueMapsUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    color: "var(--color-text-primary)",
                    textDecoration: "underline",
                    textUnderlineOffset: "2px",
                  }}
                >
                  {fLabel}
                </a>
              );
            })
          ) : (
            <span className="field-chip-empty">No fields added</span>
          )}

          {canEdit && !isAddingField && (
            <Button
              variant="primary"
              type="button"
              onClick={() => setIsAddingField(true)}
              style={{ fontSize: "0.75rem", padding: "2px 8px" }}
            >
              Add Field
            </Button>
          )}

          {canEdit && isAddingField && (
            <form
              onSubmit={handleAdd}
              style={{
                display: "inline-flex",
                gap: "4px",
                alignItems: "center",
              }}
            >
              <input
                type="text"
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                placeholder="e.g. Field 1"
                style={{
                  fontSize: "0.8rem",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  border: "1px solid var(--color-border)",
                  background: "var(--color-surface)",
                  color: "var(--color-text-primary)",
                }}
              />
              <Button variant="primary" type="submit">
                Save
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  setIsAddingField(false);
                  setFieldName("");
                }}
                style={{ fontSize: "0.75rem", padding: "2px 6px" }}
              >
                Cancel
              </Button>
            </form>
          )}
        </div>
      </div>

      {canEdit && (
        <div
          className="location-actions"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,
          }}
        >
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={() => onEdit(location)}
            title="Edit Location"
          >
            <EditIcon size="sm" />
          </Button>
          <Button
            variant="destructive"
            size="icon"
            type="button"
            onClick={() => onDelete(location)}
            title="Delete Location"
          >
            <TrashIcon size="sm" />
          </Button>
        </div>
      )}
    </div>
  );
};

LocationCard.propTypes = {
  location: PropTypes.shape({
    location_id: PropTypes.number,
    name: PropTypes.string,
    address: PropTypes.string,
    fields: PropTypes.oneOfType([
      PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAddField: PropTypes.func,
  canEdit: PropTypes.bool,
  isMapKitReady: PropTypes.bool,
};

const LeagueLocations = ({
  league,
  user,
  isWalkthrough = false,
  onReadinessChange,
}) => {
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMapKitReady, setIsMapKitReady] = useState(
    Boolean(globalThis.mapkit),
  );

  useEffect(() => {
    let isMounted = true;
    const syncLoaded = initMapKit(config.mapKitToken);
    if (syncLoaded) {
      setIsMapKitReady(true);
    } else {
      initMapKitAsync(config.mapKitToken)
        .then((loaded) => {
          if (isMounted && loaded) {
            setIsMapKitReady(true);
          }
        })
        .catch(() => {});
    }
    return () => {
      isMounted = false;
    };
  }, []);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);

  // Forms
  const [locationForm, setLocationForm] = useState({
    name: "",
    address: "",
    fields: [],
  });
  // Field input for the modal
  const [newFieldInput, setNewFieldInput] = useState("");

  const [submitStatus, setSubmitStatus] = useState("");
  const [editingLocation, setEditingLocation] = useState(null); // Location object if editing, null otherwise

  const canEdit = Boolean(
    isWalkthrough ||
    league.userRole === "Admin" ||
    league.userRole === "admin" ||
    league.isAdmin ||
    (user &&
      (user.is_super_admin ||
        (league.created_by &&
          (league.created_by === user.firebaseUid ||
            league.created_by === user.user_id ||
            league.created_by === user.id)) ||
        (league.creator_id &&
          (league.creator_id === user.user_id ||
            league.creator_id === user.id)))),
  );

  const fetchLocations = useCallback(async () => {
    try {
      const leagueId = league.league_id || league.id;
      let data =
        (await fetchData(
          `/locations?select=*,fields(*)&league_id=eq.${leagueId}`,
        )) || [];
      if (data.length > 0 && !data[0].fields) {
        try {
          const locIds = data.map((l) => l.location_id || l.id).filter(Boolean);
          const fieldsQuery =
            locIds.length > 0
              ? `/fields?location_id=in.(${locIds.join(",")})`
              : "/fields";
          const allFields = (await fetchData(fieldsQuery)) || [];
          data = data.map((l) => ({
            ...l,
            fields: allFields.filter(
              (f) => String(f.location_id) === String(l.location_id || l.id),
            ),
          }));
        } catch (fieldErr) {
          console.error("Error fetching child fields:", fieldErr);
        }
      }
      setLocations(data || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setIsLoading(false);
    }
  }, [league.league_id, league.id]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleAddFieldToForm = (e) => {
    e.preventDefault();
    if (!newFieldInput.trim()) return;

    // If editing existing location, we can optionally call API immediately,
    // but for consistency let's just update local state and save on "Save Changes".
    // NOTE: Backend supports bulk update of fields now.

    setLocationForm((prev) => ({
      ...prev,
      fields: [...prev.fields, newFieldInput.trim()],
    }));
    setNewFieldInput("");
  };

  const handleRemoveFieldFromForm = (index) => {
    setLocationForm((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
  };

  const deleteRemovedFields = async (
    existingFields,
    finalFieldSet,
    targetLocId,
  ) => {
    for (const f of existingFields) {
      const fName = typeof f === "string" ? f : f?.field_name || f?.name;
      const fId = typeof f === "object" ? f?.field_id || f?.id : null;
      if (!fName || finalFieldSet.has(fName)) continue;

      if (fId) {
        await fetchData(`/fields?field_id=eq.${fId}`, { method: "DELETE" });
      } else {
        await fetchData(
          `/fields?location_id=eq.${targetLocId}&field_name=eq.${encodeURIComponent(fName)}`,
          { method: "DELETE" },
        );
      }
    }
  };

  const addNewFields = async (finalFields, existingFields, targetLocId) => {
    if (!finalFields.length) return;
    const existingFieldNames = new Set(
      existingFields.map((f) =>
        typeof f === "string" ? f : f?.field_name || f?.name || f,
      ),
    );
    for (const fieldName of finalFields) {
      if (!existingFieldNames.has(fieldName)) {
        await fetchData("/fields", {
          method: "POST",
          body: JSON.stringify({
            location_id: targetLocId,
            field_name: fieldName,
          }),
        });
      }
    }
  };

  const syncLocationFields = async (
    targetLocId,
    existingFields,
    finalFields,
  ) => {
    if (!targetLocId) return;
    const finalFieldSet = new Set(finalFields);
    await deleteRemovedFields(existingFields, finalFieldSet, targetLocId);
    await addNewFields(finalFields, existingFields, targetLocId);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!locationForm.name?.trim()) {
      setSubmitStatus("Please enter or select a location name.");
      return;
    }

    setSubmitStatus("submitting");

    // Auto-add pending field if user forgot to click "Add"
    let finalFields = [...(locationForm.fields ?? [])];
    if (newFieldInput?.trim()) {
      finalFields.push(newFieldInput.trim());
    }

    // Deduplicate fields and remove empty strings
    finalFields = [...new Set(finalFields.filter((f) => f?.trim()))];

    // Prepare payload without fields array to prevent PostgREST 400 errors
    const payload = {
      league_id: league.league_id || league.id,
      name: locationForm.name,
      address: locationForm.address,
    };

    try {
      const url = editingLocation
        ? `/locations?location_id=eq.${editingLocation.location_id}`
        : "/locations";
      const method = editingLocation ? "PATCH" : "POST";

      const res = await fetchData(url, {
        method: method,
        headers: { Prefer: "return=representation" },
        body: JSON.stringify(payload),
      });

      const savedLocation =
        Array.isArray(res) && res[0] ? res[0] : editingLocation;
      const targetLocId =
        savedLocation?.location_id || editingLocation?.location_id;

      await syncLocationFields(
        targetLocId,
        editingLocation?.fields || [],
        finalFields,
      );

      // Refresh full list to get generated IDs for fields
      await fetchLocations();
      closeModal();
      onReadinessChange?.();
    } catch (error) {
      setSubmitStatus(`Error: ${error.message}`);
    }
  };

  // Delete Confirmation Logic
  const [locationToDelete, setLocationToDelete] = useState(null);

  const confirmDeleteLocation = (location) => {
    setLocationToDelete(location);
  };

  const cancelDelete = () => {
    setLocationToDelete(null);
  };

  const executeDeleteLocation = async () => {
    if (!locationToDelete) return;

    const locationId = locationToDelete.location_id;
    try {
      await fetchData(`/locations?location_id=eq.${locationId}`, {
        method: "DELETE",
      });
      setLocations(locations.filter((l) => l.location_id !== locationId));
      onReadinessChange?.();
    } catch (error) {
      console.error("Error deleting location:", error);
    } finally {
      setLocationToDelete(null);
    }
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    // Flatten fields to just names for the form
    const fieldNames = location.fields
      ? location.fields.map((f) =>
          typeof f === "string" ? f : f.field_name || f.name || f,
        )
      : [];
    setLocationForm({
      name: location.name,
      address: location.address,
      fields: fieldNames,
    });
    setShowAddModal(true);
  };

  const handleAddNewLocation = () => {
    setEditingLocation(null);
    setLocationForm({ name: "", address: "", fields: [] });
    setNewFieldInput("");
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingLocation(null);
    setLocationForm({ name: "", address: "", fields: [] });
    setNewFieldInput("");
    setSubmitStatus("");
  };

  const handleQuickAddField = async (location, newFieldName) => {
    if (!newFieldName?.trim()) return;
    try {
      await fetchData(`/fields`, {
        method: "POST",
        body: JSON.stringify({
          location_id: location.location_id || location.id,
          field_name: newFieldName.trim(),
        }),
      });
      await fetchLocations();
      onReadinessChange?.();
    } catch (err) {
      console.error("Failed to add field to location", err);
    }
  };

  const renderLocationsContent = () => {
    if (isLoading) {
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <div className="loading-spinner" />
        </div>
      );
    }
    if (locations.length === 0) {
      return (
        <div className="empty-state" style={{ padding: "3rem 1rem" }}>
          <p
            style={{
              fontSize: "1.1rem",
              fontWeight: "600",
              marginBottom: "0.5rem",
              color: "var(--color-text-secondary)",
            }}
          >
            No locations added yet.
          </p>
        </div>
      );
    }
    return locations.map((location) => (
      <LocationCard
        key={location.location_id}
        location={location}
        onEdit={handleEditLocation}
        onDelete={() => confirmDeleteLocation(location)}
        onAddField={handleQuickAddField}
        canEdit={canEdit}
        isMapKitReady={isMapKitReady}
      />
    ));
  };

  return (
    <div className="league-locations">
      {canEdit && (
        <div
          className="locations-header"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <Button
            variant="primary"
            type="button"
            onClick={handleAddNewLocation}
          >
            Add Location
          </Button>
        </div>
      )}

      <div
        className="locations-list"
        style={{ display: "flex", flexDirection: "column", gap: 0 }}
      >
        {renderLocationsContent()}
      </div>

      {/* Add/Edit Location Drawer */}
      <Drawer
        isOpen={showAddModal}
        onClose={closeModal}
        title={editingLocation ? "Edit Location" : "Add New Location"}
      >
        <form onSubmit={handleFormSubmit}>
          <div className="form-group">
            <label htmlFor="location-address-search">
              Address & Map Search
            </label>
            <p
              className="text-muted small-text"
              style={{ marginTop: 4, marginBottom: 8 }}
            >
              Search for a place or address, or click the crosshair icon to use
              your location.
            </p>
            {locationForm.name && (
              <div
                style={{
                  marginBottom: "8px",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  color: "var(--color-text-primary)",
                }}
                data-testid="selected-location-name"
              >
                {locationForm.name}
              </div>
            )}
            <LocationPicker
              initialAddress={locationForm.address}
              onLocationSelect={(loc) => {
                const resolvedName = loc.name || loc.address || "";
                setLocationForm((prev) => ({
                  ...prev,
                  name: resolvedName || prev.name,
                  address: loc.address || prev.address,
                }));
              }}
            />
          </div>

          <div className="form-group">
            <span className="fields-section-title">Fields</span>
            <p className="text-muted fields-help-text">
              Add fields available at this location (e.g. &quot;Field 1&quot;,
              &quot;North Diamond&quot;).
            </p>

            <div className="fields-input-row">
              <input
                value={newFieldInput}
                onChange={(e) => setNewFieldInput(e.target.value)}
                placeholder="Field Name"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddFieldToForm(e);
                  }
                }}
              />
              <Button
                variant="outline"
                type="button"
                onClick={handleAddFieldToForm}
              >
                Add
              </Button>
            </div>

            <div
              className="fields-preview"
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {locationForm.fields.map((field, idx) => (
                <div
                  key={field || idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 0",
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  <span>{field}</span>
                  <Button
                    type="button"
                    onClick={() => handleRemoveFieldFromForm(idx)}
                    className="field-remove-btn"
                    aria-label={`Remove ${field}`}
                  >
                    <TrashIcon size="xs" />
                  </Button>
                </div>
              ))}
              {locationForm.fields.length === 0 && (
                <span className="text-muted field-empty-edit">
                  No fields added yet.
                </span>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <Button variant="outline" type="button" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"

              disabled={
                submitStatus === "submitting" || !locationForm.name?.trim()
              }
            >
              {(() => {
                if (submitStatus === "submitting") return "Saving...";
                return editingLocation ? "Save Changes" : "Save Location";
              })()}
            </Button>
          </div>
        </form>
      </Drawer>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!locationToDelete}
        onClose={cancelDelete}
        onConfirm={executeDeleteLocation}
        title="Delete Location?"
        confirmText="Delete"
        type="danger"
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{locationToDelete?.name}</strong>? This action cannot be
          undone.
        </p>
      </ConfirmationModal>
    </div>
  );
};

LeagueLocations.propTypes = {
  league: PropTypes.shape({
    id: PropTypes.string.isRequired,
    userRole: PropTypes.string,

    league_id: PropTypes.string,
  }).isRequired,
  user: PropTypes.shape({
    getIdToken: PropTypes.func, // Optional now
    is_super_admin: PropTypes.bool,
  }),
  isWalkthrough: PropTypes.bool,
  onReadinessChange: PropTypes.func,
};

export default LeagueLocations;
